import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'
import { useContext } from 'react'
import { hasPendingEligibilityCheck } from '../../components/Monitoring/EligibilityCheck/useEligibilityStatus'
import AppContext from '../../context/appContext'
import { DEFAULT_STALE_TIME_MS } from '../../queryClient'
import createEligibilityCheck from '../../services/ruutsApi/createEligibilityCheck'
import {
  type EligibilityCheckMeta,
  type EligibilityCheckQuery,
  getEligibilityCheckWithMeta,
} from '../../services/ruutsApi/getEligibilityCheck'
import type { Paginated } from '../../services/ruutsApi/normalize'
import type { EligibilityArea } from '../../types/apiModels'
import { isEligibilityEnabled } from '../../utils/programConfig/areaSubtractions'
import { queryKeys } from './queryKeys'

export type { EligibilityCheckQuery }

// Single source of truth shared by every eligibility consumer (accordion context,
// attestation report, sample planning); a module-level reference also keeps the
// query key stable across renders.
export const ELIGIBILITY_QUERY: EligibilityCheckQuery = {
  filterAreas: ['forest', 'otbnRedAreas'],
}

export const ELIGIBILITY_POLL_INTERVAL_MS = 30_000

// The rows only change when the deferred detection job persists them, and each read waits on the
// science-API OTBN lookup. gcTime matches so the entry survives navigating between the screens that
// read it.
export const ELIGIBILITY_CACHE_TIME_MS = 30 * 60_000

// The backend is the source of truth via `meta.hasEligibilityCheck`, but a check that ran and
// found areas is unambiguous proof on its own — shared here so the returned flag and the poll
// predicate can never disagree if `meta` is ever missing from a response.
const resolveHasEligibilityCheck = (
  data?: Paginated<EligibilityArea, EligibilityCheckMeta>,
): boolean => data?.meta?.hasEligibilityCheck === true || (data?.items?.length ?? 0) > 0

// Every reader, not only the screen that started a detection, must pick it up when it lands (a re-run
// over existing results never trips the poll), and a failed read must recover on focus, so until the
// result is settled the read keeps the global stale window and focus refetch.
const isSettled = (
  farmId: string,
  { status, data }: { status: string; data?: Paginated<EligibilityArea, EligibilityCheckMeta> },
) => status !== 'error' && resolveHasEligibilityCheck(data) && !hasPendingEligibilityCheck(farmId)

export const eligibilityCheckQueryOptions = ({
  farmId,
  query,
}: {
  farmId: string
  query?: EligibilityCheckQuery
}) =>
  queryOptions({
    queryKey: queryKeys.eligibilityCheck(farmId, query),
    queryFn: ({ signal }) => getEligibilityCheckWithMeta({ farmId, query, signal }),
    staleTime: ({ state }) =>
      isSettled(farmId, state) ? ELIGIBILITY_CACHE_TIME_MS : DEFAULT_STALE_TIME_MS,
    gcTime: ELIGIBILITY_CACHE_TIME_MS,
    refetchOnWindowFocus: ({ state }) => !isSettled(farmId, state),
  })

export const useEligibilityCheck = ({
  farmId,
  query,
  isCheckRunning = false,
}: {
  farmId: string | undefined
  query?: EligibilityCheckQuery
  isCheckRunning?: boolean
}) => {
  const { programConfig } = useContext(AppContext)
  const eligibilityEnabled = isEligibilityEnabled(programConfig)
  const result = useQuery({
    ...eligibilityCheckQueryOptions({ farmId: farmId as string, query }),
    enabled: !!farmId && eligibilityEnabled,
    // The detection is a deferred backend job with no status endpoint and `hasEligibilityCheck` stays
    // false until it persists its rows, so polling is the only way the result shows up without a reload.
    refetchInterval: ({ state }) =>
      isCheckRunning && !resolveHasEligibilityCheck(state.data)
        ? ELIGIBILITY_POLL_INTERVAL_MS
        : false,
  })

  const createMutation = useMutation({
    mutationFn: () => {
      if (!farmId) {
        throw new Error('Farm ID is required to create an eligibility check.')
      }
      return createEligibilityCheck({ farmId })
    },
    // The accordion shows its own error toast.
    meta: { suppressGlobalError: true },
  })

  return {
    eligibilityCheckAreas: result.data?.items ?? null,
    hasEligibilityCheck: resolveHasEligibilityCheck(result.data),
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
    refetchEligibilityCheck: result.refetch,
    isFetchingEligibilityCheck: result.isFetching,
    createEligibilityCheck: createMutation.mutateAsync,
  }
}
